import { assetsUrl } from '@/constants/strapi.constants';
import type { Image as FrontImage, StrapiFile } from '@/interfaces/strapi';
import type { ImageModel, StrapiFileModel, StrapiImageFormats } from '@/interfaces/model';

export type FormatKey = keyof StrapiImageFormats;

/* -------------------------- URL utils -------------------------- */
export const joinUrl = (base: string, path: string) => {
	if (!base) return path ?? '';
	if (!path) return base;
	const b = base.endsWith('/') ? base.slice(0, -1) : base;
	const p = path.startsWith('/') ? path : `/${path}`;
	return `${b}${p}`;
};

export const toAbsolute = (url: string, base: string = assetsUrl) =>
	/^https?:\/\//i.test(url) ? url : joinUrl(base, url);

/* ----------------------- Imagen: formatos ---------------------- */
export function pickFormat(formats: StrapiImageFormats | null | undefined, order: FormatKey[]) {
	if (!formats) return null;
	for (const key of order) {
		const fmt = formats[key];
		if (fmt) return fmt;
	}
	return null;
}

export const bestImageUrl = (img: ImageModel) =>
	img.formats?.large?.url ??
	img.formats?.medium?.url ??
	img.formats?.small?.url ??
	img.formats?.thumbnail?.url ??
	img.url ??
	'';

export const bestThumbUrl = (img: ImageModel) =>
	img.formats?.thumbnail?.url ??
	img.formats?.small?.url ??
	img.formats?.medium?.url ??
	img.formats?.large?.url ??
	img.url ??
	'';

/* ------------------------ Mapeadores base ---------------------- */
export const mapImage = (img: ImageModel, prefer: 'full' | 'thumb' = 'full'): FrontImage => {
	const chosen = prefer === 'thumb' ? bestThumbUrl(img) : bestImageUrl(img);
	return {
		url: toAbsolute(chosen),
		alt: img.alternativeText ?? img.caption ?? img.name ?? '',
	};
};

export const mapGalleryFrom = (
	images?: ImageModel[],
): { images: Array<{ thumbnail: FrontImage; image: FrontImage }> } => {
	const list = Array.isArray(images) ? images : [];
	return {
		images: list.map((im) => ({
			thumbnail: mapImage(im, 'thumb'),
			image: mapImage(im, 'full'),
		})),
	};
};

export const mapStrapiFile = (file: StrapiFileModel | null | undefined): StrapiFile => {
	if (!file) {
		// @ts-expect-error ajusta si tu tipo no admite undefined
		return undefined;
	}
	return {
		id: file.id,
		documentId: file.documentId,
		name: file.name,
		url: toAbsolute(file.url),
		mime: file.mime,
		ext: file.ext,
		size: file.size,
		createdAt: file.createdAt,
		updatedAt: file.updatedAt,
		publishedAt: file.publishedAt,
	} as unknown as StrapiFile;
};
